// ═══════════════════════════════════════════════════════════
// V2 API Client
//
// All requests route through /api/v2proxy (Vite dev middleware
// or Cloudflare Pages Function in production). Auth is handled
// server-side via HTTP-only cookies — the client never stores
// or injects GAB tokens.
// ═══════════════════════════════════════════════════════════

import type { PaginatedResponse, QueryOptions, GABUser } from './types';

const API_BASE = '/api/v2proxy';
const APP_ID = import.meta.env.VITE_V2_APP_ID;

// ── Auth endpoints ──────────────────────────────────────
// These hit /api/auth/* — handled by Vite dev middleware or
// Cloudflare Pages Functions in production.

export async function gabLogin(email: string, password: string): Promise<{ user: GABUser }> {
  const res = await fetch('/api/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: 'Login failed' })) as { error?: string };
    throw new Error(body.error ?? 'Login failed');
  }
  return res.json();
}

export async function gabLogout(): Promise<void> {
  await fetch('/api/auth/logout', {
    method: 'POST',
    credentials: 'include',
  });
}

export async function gabCheckSession(): Promise<boolean> {
  try {
    const res = await fetch('/api/auth/me', { credentials: 'include' });
    return res.ok;
  } catch {
    return false;
  }
}

// ── Core Fetch ──────────────────────────────────────────
// Cookies are sent automatically via credentials: 'include'.
// The proxy reads gab_token cookie and injects Authorization.
// If no cookie, the proxy injects the admin token (public portal).

async function apiFetch<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const isMultipart = options.body instanceof FormData;
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    credentials: 'include',
    headers: isMultipart ? options.headers : {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!res.ok) {
    const body = await res.text().catch(() => '');
    console.error(`[API ${res.status}] ${path}`, body);
    throw new Error(`API ${res.status}: ${path} — ${body}`);
  }
  return res.status === 204 ? (undefined as T) : res.json();
}

// ── Table path helper ───────────────────────────────────

function tablePath(tableKey: string) {
  return `/v2/apps/${APP_ID}/tables/${tableKey}/records`;
}

// ── Response normalizer ─────────────────────────────────

function normalizeListResponse<T>(raw: unknown): PaginatedResponse<T> {
  const r = raw as Record<string, unknown>;
  const records = (r.records ?? r.items ?? r.data ?? []) as T[];
  return { records, total: (r.total as number) ?? records.length };
}

// ── CRUD Operations ─────────────────────────────────────

export async function fetchRecords<T = Record<string, unknown>>(
  tableKey: string,
  opts?: { limit?: number; offset?: number; sort?: { field: string; direction: 'asc' | 'desc' } },
): Promise<PaginatedResponse<T>> {
  const params = new URLSearchParams();
  if (opts?.limit) params.set('limit', String(opts.limit));
  if (opts?.offset) params.set('offset', String(opts.offset));
  if (opts?.sort) {
    params.set('sort', opts.sort.field);
    params.set('order', opts.sort.direction);
  }
  const qs = params.toString();
  const path = `${tablePath(tableKey)}${qs ? `?${qs}` : ''}`;
  const raw = await apiFetch<unknown>(path);
  return normalizeListResponse<T>(raw);
}

// GAB's POST /records/query endpoint exists but ignores filter params — it
// returns all records regardless. Filtering must be done client-side after
// fetching a sufficiently large page.
export async function queryRecords<T = Record<string, unknown>>(
  tableKey: string,
  query: QueryOptions,
  opts?: { anonymous?: boolean },
): Promise<PaginatedResponse<T>> {
  void opts;

  const params = new URLSearchParams();
  params.set('limit', String(query.limit ?? 500));
  if (query.sort) {
    params.set('sort', query.sort.field);
    params.set('order', query.sort.direction);
  }

  const raw = await apiFetch<unknown>(`${tablePath(tableKey)}?${params}`);
  const result = normalizeListResponse<T>(raw);

  // Apply filters client-side
  if (query.filters && query.filters.length > 0) {
    result.records = result.records.filter(row => {
      return query.filters!.every(f => {
        const val = (row as Record<string, unknown>)[f.field];
        const fv = f.value;
        switch (f.operator) {
          case 'eq':           return String(val ?? '') === String(fv ?? '');
          case 'neq':          return String(val ?? '') !== String(fv ?? '');
          case 'gt':           return Number(val) > Number(fv);
          case 'gte':          return Number(val) >= Number(fv);
          case 'lt':           return Number(val) < Number(fv);
          case 'lte':          return Number(val) <= Number(fv);
          case 'contains':     return String(val ?? '').toLowerCase().includes(String(fv ?? '').toLowerCase());
          case 'not_contains': return !String(val ?? '').toLowerCase().includes(String(fv ?? '').toLowerCase());
          case 'starts_with':  return String(val ?? '').toLowerCase().startsWith(String(fv ?? '').toLowerCase());
          case 'ends_with':    return String(val ?? '').toLowerCase().endsWith(String(fv ?? '').toLowerCase());
          case 'is_empty':     return !val || String(val) === '';
          case 'is_not_empty': return !!val && String(val) !== '';
          case 'in':           return Array.isArray(fv) && fv.map(String).includes(String(val ?? ''));
          case 'nin':          return Array.isArray(fv) && !fv.map(String).includes(String(val ?? ''));
          case 'between':      return Array.isArray(fv) && fv.length === 2 && Number(val) >= Number(fv[0]) && Number(val) <= Number(fv[1]);
          default:             return true;
        }
      });
    });
    result.total = result.records.length;
  }

  return result;
}

export async function createRecord<T = Record<string, unknown>>(
  tableKey: string,
  data: Record<string, unknown>,
  opts?: { anonymous?: boolean },
): Promise<T> {
  void opts;
  return apiFetch<T>(tablePath(tableKey), {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateRecord<T = Record<string, unknown>>(
  tableKey: string,
  recordId: number,
  data: Record<string, unknown>,
  opts?: { anonymous?: boolean },
): Promise<T> {
  void opts;
  return apiFetch<T>(`${tablePath(tableKey)}/${recordId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function fetchRecord<T = Record<string, unknown>>(
  tableKey: string,
  recordId: number,
  opts?: { anonymous?: boolean },
): Promise<T | null> {
  void opts;
  try {
    return await apiFetch<T>(`${tablePath(tableKey)}/${recordId}`);
  } catch (e) {
    if (e instanceof Error && e.message.includes('404')) return null;
    throw e;
  }
}

export async function deleteRecord(
  tableKey: string,
  recordId: number,
): Promise<void> {
  await apiFetch<void>(`${tablePath(tableKey)}/${recordId}`, { method: 'DELETE' });
}

export async function bulkCreate<T = Record<string, unknown>>(
  tableKey: string,
  records: Record<string, unknown>[],
): Promise<T[]> {
  return apiFetch<T[]>(`${tablePath(tableKey)}/bulk`, {
    method: 'POST',
    body: JSON.stringify({ records }),
  });
}

export async function bulkDelete(
  tableKey: string,
  ids: number[],
): Promise<void> {
  await apiFetch<void>(`${tablePath(tableKey)}/bulk`, {
    method: 'DELETE',
    body: JSON.stringify({ ids }),
  });
}
